import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MeteorObservable } from 'meteor-rxjs';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  shareReplay,
  Subscription,
} from 'rxjs';
import { defaultCancelReasons } from 'server/helpers/defaultCancelReasons';
import { Customers } from 'server/shared/collections/customers';
import { Users } from 'server/shared/collections/users';
import { checkCanCancelEmployment } from 'server/shared/helpers/checkCanCancelEmployment';
import {
  EvaluatedEmploymentStatusEnum,
  getEvaluatedEmploymentStatus,
} from 'server/shared/helpers/getEvaulatedEmploymentStatus';
import { filterOverviewInviteesForEmployment } from 'server/shared/helpers/job/filterOverviewInvitees';
import { CancelReason } from 'server/shared/models/cancelReason';
import { Customer } from 'server/shared/models/customer';
import {
  Employment,
  EmploymentStatusEnum,
} from 'server/shared/models/employment';
import { EmploymentInvitee } from 'server/shared/models/employmentInvitee';
import { EmploymentInviteeStatusEnum } from 'server/shared/models/employmentInviteeStatusEnum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser } from 'server/shared/models/user';
import { prepareEmploymentInvitee } from '../../../helpers/employment/prepareEmploymentInvitee';
import { sortByMatchPercentage } from '../../../helpers/employment/sortByMatchPercentage';
import { filterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { CustomerService } from '../../services/customer.service';
import { EmploymentService } from '../../services/employment.service';
import { PoolerService } from '../../services/pooler/pooler.service';
const statusOrder = [
  EmploymentInviteeStatusEnum.BOOKED,
  EmploymentInviteeStatusEnum.CANCELLED,
];
function sortByStatusOrder(a: EmploymentInvitee, b: EmploymentInvitee) {
  return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
}

@Component({
  selector: 'app-employment-list-item',
  templateUrl: './employment-list-item.component.html',
  styleUrls: ['./employment-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmploymentListItemComponent
  implements OnInit, OnChanges, OnDestroy
{
  loggedInUserTracker?: Tracker.Computation;
  customerOfEmploymentTracker?: Tracker.Computation;
  employment$ = new BehaviorSubject<Employment | undefined>(undefined);
  @Input() set employment(employment: Employment | undefined) {
    this.employment$.next(employment);
  }
  get employment(): Employment | undefined {
    return this.employment$.getValue();
  }

  usersOfEmploymentSubscription?: Subscription;
  cancelReasons: CancelReason[] = [];

  @Input() currentCustomer?: Customer;

  canCancel$ = combineLatest([
    this.employment$.pipe(filter(filterIsNotEmpty)),
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(([employment, customer]) => {
      this.cancelReasons = customer?.cancelReasons || defaultCancelReasons;
      return checkCanCancelEmployment(employment, customer);
    }),
  );

  evaluatedEmploymentStatus: EvaluatedEmploymentStatusEnum =
    EvaluatedEmploymentStatusEnum.PENDING;

  overviewInvitees$ = combineLatest([
    this.employment$.pipe(filter(Boolean)),
    this.poolerService.poolerUsersById$,
    this.customerService.readyDispoCustomer$,
    this.poolerService.readyPoolerCustomerMap$,
  ]).pipe(
    map(([employment, poolerUsersById, currentCustomer, poolerMap]) => {
      const overviewStatusInvitees =
        filterOverviewInviteesForEmployment(employment);
      return overviewStatusInvitees
        .map((invitee) =>
          prepareEmploymentInvitee(
            invitee,
            poolerUsersById,
            employment,
            currentCustomer,
            poolerMap,
          ),
        )
        .sort(sortByMatchPercentage)
        .sort(sortByStatusOrder);
    }),
    shareReplay(1),
  );

  loggedInCustomer$ = this.customerService.readyDispoCustomer$;

  constructor(
    private ngZone: NgZone,
    private customerService: CustomerService,
    private employmentService: EmploymentService,
    private changeDetectorRef: ChangeDetectorRef,
    private poolerService: PoolerService,
  ) {}
  loggedInUser?: DispoUser;
  loggedInCustomer?: Customer;
  customerOfEmployment$ = new BehaviorSubject<Customer | undefined>(undefined);

  ngOnInit(): void {
    this.loggedInUserTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.loggedInUser = Users.findOne({
          _id: Meteor.userId(),
          role: RoleEnum.DISPO,
        }) as DispoUser;
        this.loggedInCustomer = Customers.findOne({
          _id: this.loggedInUser?.customerId,
        });
      });
    });

    this.customerOfEmploymentTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        let customerOfEmployment = Customers.findOne({
          _id: this.employment?.customer.id,
        });
        this.customerOfEmployment$.next(customerOfEmployment);
      });
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.employment) {
      this.updateStatus(changes.employment.currentValue).then();
    }
  }

  ngOnDestroy() {
    if (this.usersOfEmploymentSubscription) {
      this.usersOfEmploymentSubscription.unsubscribe();
    }
    if (this.loggedInUserTracker) {
      this.loggedInUserTracker.stop();
    }
    if (this.customerOfEmploymentTracker) {
      this.customerOfEmploymentTracker.stop();
    }
  }

  subscribeUsersOfEmployment(employmentId: string) {
    if (this.usersOfEmploymentSubscription) {
      this.usersOfEmploymentSubscription.unsubscribe();
    }

    this.usersOfEmploymentSubscription = MeteorObservable.subscribe(
      'usersOfEmployment',
      employmentId,
      true,
    ).subscribe();
  }

  async updateStatus(employment?: Employment) {
    if (!employment) return;
    const customer = await this.customerService.readyDispoCustomer;
    this.evaluatedEmploymentStatus = getEvaluatedEmploymentStatus(
      employment,
      customer,
    );
    this.changeDetectorRef.detectChanges();
  }

  async cancelEmployment() {
    if (!this.employment) {
      return;
    }
    await this.employmentService.cancelEmployment(
      this.employment._id,
      this.cancelReasons,
    );
  }

  async duplicateEmployment() {
    if (!this.employment) {
      return;
    }
    await this.employmentService.duplicateJob(this.employment);
  }

  readonly EmploymentStatusEnum = EmploymentStatusEnum;
  readonly EvaluatedEmploymentStatusEnum = EvaluatedEmploymentStatusEnum;
}
